import { fetchPolicyFilters } from '../../policiesSlice'
import { getFiltersListSorted } from '../../policiesSelectors'
import { RootState } from '../../../../rootReducer'
import { IGetFiltersParams, IPolicyFilter } from '../../../../interfaces'
import { POLICY_FILTER_TYPES } from '../../../../constants'
import DateElement from '../../../../components/DateElement/dateElement'
import Table, { ITableColumn } from '../../../../components/dataTable/dataTable_V2'
import RegularColumn from '../../../../components/dataTable/components/regularColumn'
import TableLegend from '../../../../components/TableLegend'
import { getShowLoader } from '../../../../reducers/requestReducer'
import ActionsPopup from '../../../../components/ActionsPopup/actionsPopup'
import NoDataFound from '../../../../components/NoDataFound'
import { usePermission } from '../../../../HOCs/PermissionProvider'
import { ACTIONS } from '../../../../components/PermissionMapper/constants'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import React, { useEffect } from 'react'

interface IProps {
  activeFilterTypeId: string
  fetchPolicyFilters: (params: IGetFiltersParams) => void
  list?: IPolicyFilter[]
  total?: number
  showLoader: boolean
}

const FiltersTableList = ({
  activeFilterTypeId,
  fetchPolicyFilters,
  list = [],
  total = 0,
  showLoader
}: IProps): React.ReactElement => {
  const intl = useIntl()
  useEffect(() => {
    if (activeFilterTypeId) {
      const activeFilter = POLICY_FILTER_TYPES.find(
        (filterType) => filterType.id === activeFilterTypeId
      )
      activeFilter && fetchPolicyFilters({ type: activeFilter.filter })
    }
  }, [fetchPolicyFilters, activeFilterTypeId])

  const transName = intl.formatMessage({ id: 'filters.table.name' })
  const transOwner = intl.formatMessage({ id: 'filters.table.owner' })
  const transCreatedDate = intl.formatMessage({ id: 'filters.table.createdDate' })
  const transEdit = intl.formatMessage({ id: 'filter.action.edit' })
  const transDelete = intl.formatMessage({ id: 'filter.action.delete' })

  /**Todo: dericve a type for column configuration */
  const columns: ITableColumn[] = [
    {
      title: transName,
      dataKey: 'name',
      minWidth: 200,
      flex: 1.5,
      cellRenderer: function RegularRenderer({ data: filter }) {
        const { name = '' } = filter
        return <RegularColumn content={name} />
      }
    },
    {
      title: transOwner,
      dataKey: 'owner',
      minWidth: 200,
      cellRenderer: function RegularRenderer({ data: filter }) {
        const { owner = '' } = filter
        return <RegularColumn content={owner} />
      }
    },
    {
      title: transCreatedDate,
      dataKey: 'createdAt',
      minWidth: 200,
      cellRenderer: function RegularRenderer({ data: filter }) {
        const { createdAt = '' } = filter
        return <RegularColumn content={<DateElement time={createdAt} />} />
      }
    }
  ]
  if (usePermission()?.hasAction(ACTIONS.RULE_SET_CREATE)) {
    columns.push({
      title: intl.formatMessage({ id: 'actions' }),
      dataKey: 'table-actions',
      headerClass: 'ag-content-centered',
      minWidth: 100,
      center: true,
      resizable: false,
      cellRenderer: function RegularRenderer() {
        return (
          <RegularColumn
            tooltip={intl.formatMessage({ id: 'actions' })}
            content={
              <ActionsPopup
                content={
                  <>
                    <button title={transEdit} className="btn">
                      {intl.formatMessage({ id: 'btn.edit' })}
                    </button>
                    <button title={transDelete} className="btn">
                      {intl.formatMessage({ id: 'btn.delete' })}
                    </button>
                  </>
                }
              />
            }
          />
        )
      }
    })
  }

  return (
    <div
      data-test-id="policies-filters-table-list"
      className={`xs-mb-24 ${total === 0 ? 'xs-mt-24' : 'xs-mt-16'}`}
    >
      <div className="content-wrapper xs-p-0">
        {(total > 0 || showLoader) && (
          <div className="policy-filters-table-container">
            <Table
              showLoader={showLoader}
              columns={columns}
              data={list}
              customHeader={
                total > 0 ? (
                  <TableLegend
                    total={total}
                    showing={total}
                    page={1}
                    name={intl.formatMessage({ id: 'table.legend.filters' })}
                    className="xs-m-0"
                  />
                ) : undefined
              }
            />
          </div>
        )}
        {!showLoader && total === 0 && <NoDataFound className="no-data-found" />}
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    list: getFiltersListSorted(state),
    total: state.policies.filters.total,
    showLoader: getShowLoader()
  }
}

const mapDispatchToProps = { fetchPolicyFilters }

export default connect(mapStateToProps, mapDispatchToProps)(FiltersTableList)
